import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import fileService from '../../services/fileService';
import folderService from '../../services/folderService';

interface RenameDialogProps {
  open: boolean;
  onClose: () => void;
  item: {
    id: string;
    name: string;
    type: 'file' | 'folder';
  } | null;
  onSuccess?: () => void;
}

const RenameDialog: React.FC<RenameDialogProps> = ({
  open,
  onClose,
  item,
  onSuccess
}) => {
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (item && open) {
      setNewName(item.name);
      setError('');
    }
  }, [item, open]);

  const handleClose = () => {
    if (!isLoading) {
      setNewName('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item) return;

    const trimmedName = newName.trim();
    if (!trimmedName) {
      setError('Name cannot be empty');
      return;
    }

    if (trimmedName === item.name) {
      handleClose();
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (item.type === 'file') {
        await fileService.renameFile(item.id, trimmedName);
        enqueueSnackbar('File renamed successfully', { variant: 'success' });
      } else {
        await folderService.renameFolder(item.id, trimmedName);
        enqueueSnackbar('Folder renamed successfully', { variant: 'success' });
      }
      
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error('Error renaming item:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to rename item';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit(e);
    }
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Rename {item.type === 'file' ? 'File' : 'Folder'}
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isLoading}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new name for "{item.name}"
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            error={!!error}
            helperText={error}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{ minWidth: 80 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !newName.trim() || newName.trim() === item.name}
            sx={{ minWidth: 80 }}
          >
            {isLoading ? 'Renaming...' : 'Rename'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RenameDialog; 